import { EVENT_CATEGORIES, EventFilterState, UNTAGGED } from "../types/event";

/**
 * Remembers the calendar's filters between visits, as v2 does.
 *
 * v2 keeps three separate cookies — EventText, EventLocation, EventType — read
 * and written by helpers inlined in its Home.tsx. This keeps the behaviour and
 * changes two things:
 *
 *   - ONE cookie holding JSON, rather than three. The three are always read and
 *     written together, and v2's EventType is a single category string while
 *     this app's is a list, so reusing that name would hand v2 a value it cannot
 *     parse if both apps are ever served from the same domain.
 *   - SameSite=Lax, which v2 omits.
 *
 * NOT PII. These are an event title search term, a list of category ids and a
 * few booleans — none of it identifies a person, which is what makes a cookie
 * acceptable here at all (see CLAUDE.md on browser handling). Nothing about an
 * attendee, carer or student may be added to this.
 *
 * A `locationText` written by an older version is simply dropped on read: the
 * location filter is gone, and a saved value with no control to clear it would
 * be a filter nobody could see or turn off. Read strictly by key, so no
 * migration and no cookie rename is needed for that.
 *
 * A cookie rather than localStorage because it is what v2 uses and the ask was
 * to match it. Worth knowing: cookies ride along on every request to the
 * domain, so this handful of bytes is sent to the API on each call. localStorage
 * would not be, and would suit the job better if that ever matters.
 */
/**
 * Versioned, and the version is part of the name.
 *
 * The category ids changed when the API retired `mypymble` for events created
 * here in favour of `manual`. A saved cookie still holding the old ids is not
 * merely stale: the filter is an allow-list, so it would go on hiding every
 * manually created event for anyone who had used the calendar before, and it
 * would look like the events had been deleted. A new name retires the old
 * cookie rather than trying to migrate what is in it.
 */
const COOKIE = "em3.eventFilters.v2";
const MAX_AGE_DAYS = 30;

/** Every known category id, used to validate what comes back out of the cookie. */
export const ALL_CATEGORY_IDS = EVENT_CATEGORIES.map((c) => c.id);

/** The sources a new person sees: the ones with something to manage. */
export const DEFAULT_CATEGORY_IDS = EVENT_CATEGORIES.filter(
	(c) => c.defaultOn,
).map((c) => c.id);

export const defaultFilters = (): EventFilterState => ({
	searchText: "",
	categories: [...DEFAULT_CATEGORY_IDS],
	eventType: "",
	groups: [],
	tags: [],
	// Off, both of them: the calendar has always shown everything, and a filter
	// nobody chose is a filter nobody knows to turn off.
	hideUnapproved: false,
	schoolDayOnly: false,
	permissionOnly: false,
});

/** Whatever of `value` is a list of strings, or an empty list. */
function stringList(value: unknown): string[] {
	return Array.isArray(value)
		? value.filter((v): v is string => typeof v === "string")
		: [];
}

function readCookie(name: string): string | null {
	for (const part of document.cookie.split(";")) {
		const entry = part.trim();
		if (entry.startsWith(`${name}=`)) {
			return entry.slice(name.length + 1);
		}
	}
	return null;
}

/**
 * Saved filters, falling back to the defaults.
 *
 * Everything is validated on the way out rather than trusted: a cookie is
 * user-editable, and one hand-edited value should give someone the default
 * calendar, not a crash on a page that has not rendered yet. Unknown category
 * ids are dropped so a renamed category cannot leave a filter that matches
 * nothing and looks like an empty calendar.
 */
export function readFilters(): EventFilterState {
	const fallback = defaultFilters();
	try {
		const raw = readCookie(COOKIE);
		if (!raw) return fallback;

		const parsed: unknown = JSON.parse(decodeURIComponent(raw));
		if (!parsed || typeof parsed !== "object") return fallback;

		const saved = parsed as Partial<Record<keyof EventFilterState, unknown>>;
		/* Keys the state no longer HAS, read separately because `saved` is typed
		   off the current shape. Only `eventTypes` needs carrying over — see the
		   field below; `locationText` is deliberately dropped. */
		const legacy = parsed as { eventTypes?: unknown };
		const categories = Array.isArray(saved.categories)
			? saved.categories.filter(
					(id): id is string =>
						typeof id === "string" && ALL_CATEGORY_IDS.includes(id),
				)
			: fallback.categories;

		return {
			searchText: typeof saved.searchText === "string" ? saved.searchText : "",
			// An empty saved list is a real choice (everything unticked), so it is
			// kept — only a missing or malformed one falls back to all ticked.
			categories,
			// The three facets added with the redesigned rail. Absent in a cookie
			// written by the older rail, and absent is exactly the default, so
			// there is nothing to migrate and the cookie name does not change.
			// Their values are open vocabularies — an `EventType` string, an
			// audience group id — so there is no list to validate against; strings
			// are all that is required, and an id nothing matches simply shows an
			// empty calendar with a visible tick to clear.
			/* A LIST here is a cookie from the multi-select rail. Its first value
			   is taken rather than the whole thing dropped, so someone who had one
			   type chosen — which was nearly everyone, see EventFilterState — keeps
			   the filter they left running. */
			eventType: Array.isArray(legacy.eventTypes)
				? (stringList(legacy.eventTypes)[0] ?? "")
				: typeof saved.eventType === "string"
					? saved.eventType
					: "",
			groups: stringList(saved.groups),
			/* Same reasoning as the three above, and the same non-migration: absent
			   in a cookie written before tags existed, and absent is the default.

			   Not validated against the catalogue, deliberately. Tags are an open
			   vocabulary and the catalogue is one request that has not happened
			   yet when this is read, so checking against it would silently drop a
			   perfectly good saved filter on every cold start. A tag nothing
			   matches shows an empty calendar with a visible tick to clear, which
			   is the same outcome the other three accept.

			   The ONE value that is dropped is `__none`, the old "Untagged" row.
			   That row is gone from the rail, so a cookie still carrying its tick
			   would filter the calendar to untagged events with nothing on screen
			   to turn it off — the exact stuck state the paragraph above says a
			   visible tick saves everyone from. */
			tags: stringList(saved.tags).filter((t) => t !== UNTAGGED),
			/* `=== true` rather than a cast, so anything else a hand-edited
			   cookie holds — a string, a number, absent because it was written
			   before this existed — lands on the default of showing everything.
			   Getting this wrong in the other direction would hide most of the
			   calendar on a cold start and look like the events were gone. */
			hideUnapproved: saved.hideUnapproved === true,
			schoolDayOnly: saved.schoolDayOnly === true,
			permissionOnly: saved.permissionOnly === true,
		};
	} catch {
		// Deliberately silent: a malformed cookie is not worth a console line,
		// and the fallback is a working calendar.
		return fallback;
	}
}

export function writeFilters(filters: EventFilterState): void {
	try {
		const expires = new Date(
			Date.now() + MAX_AGE_DAYS * 24 * 60 * 60 * 1000,
		).toUTCString();
		const value = encodeURIComponent(JSON.stringify(filters));
		document.cookie = `${COOKIE}=${value}; expires=${expires}; path=/; SameSite=Lax`;
	} catch {
		// Cookies can be disabled outright. Losing the preference is survivable;
		// failing to render the calendar is not.
	}
}

/**
 * Whether the filter rail is collapsed to its narrow strip.
 *
 * Its own cookie rather than a field on EventFilterState: this is a layout
 * preference, not a filter, and putting it in there would make it part of what
 * `filters` means to the calendar — including its query key — for no reason.
 * Saved because a rail that springs back open on every visit is not collapsed
 * in any useful sense.
 */
const RAIL_COOKIE = "em3.filterRail.collapsed";

export function readRailCollapsed(): boolean {
	return readCookie(RAIL_COOKIE) === "1";
}

export function writeRailCollapsed(collapsed: boolean): void {
	try {
		const expires = new Date(
			Date.now() + MAX_AGE_DAYS * 24 * 60 * 60 * 1000,
		).toUTCString();
		document.cookie = `${RAIL_COOKIE}=${collapsed ? "1" : "0"}; expires=${expires}; path=/; SameSite=Lax`;
	} catch {
		// See writeFilters: losing the preference is survivable.
	}
}

/**
 * Which of the five views was last open.
 *
 * Its own cookie, for the same reason as the rail: a view is a layout choice,
 * not a filter, and folding it into EventFilterState would make it part of what
 * `filters` means to the calendar — including the query key the API is asked
 * with.
 *
 * Saved because leaving the page is not a decision to change view. Opening an
 * event and coming back put everyone in Month regardless of where they had been
 * reading, and the way back to a list they had scrolled was to find it again.
 *
 * The value is VALIDATED on the way out rather than trusted. A cookie is
 * editable, the views have changed once already — Agenda was retired when List
 * arrived — and an unknown name would index the Mobiscroll view map to
 * undefined and take the calendar down with it.
 */
const VIEW_COOKIE = "em3.calendarView";

const KNOWN_VIEWS = ["list", "cards", "month", "week", "day"] as const;

export type StoredView = (typeof KNOWN_VIEWS)[number];

export function readView(fallback: StoredView = "month"): StoredView {
	const saved = readCookie(VIEW_COOKIE);
	return KNOWN_VIEWS.includes(saved as StoredView)
		? (saved as StoredView)
		: fallback;
}

export function writeView(view: StoredView): void {
	try {
		const expires = new Date(
			Date.now() + MAX_AGE_DAYS * 24 * 60 * 60 * 1000,
		).toUTCString();
		document.cookie = `${VIEW_COOKIE}=${view}; expires=${expires}; path=/; SameSite=Lax`;
	} catch {
		// See writeFilters: losing the preference is survivable.
	}
}

/**
 * Whether the schedule views draw the bump in / bump out bands.
 *
 * Its own cookie, for the same reason as the rail and the view: this is a
 * display preference, not a filter. Folding it into EventFilterState would make
 * it part of what `filters` means to the calendar — including the query key the
 * API is asked with — and turning a band off would refetch the month.
 *
 * DEFAULTS ON. The bands only appear on events that carry a bump time at all,
 * so the default costs nothing to anyone not using them, and a feature that
 * ships switched off is one nobody finds. `=== "0"` rather than `=== "1"` puts
 * a malformed or absent cookie on that default.
 */
const BUMP_COOKIE = "em3.calendar.bumpTimes";

export function readShowBumpTimes(): boolean {
	return readCookie(BUMP_COOKIE) !== "0";
}

export function writeShowBumpTimes(show: boolean): void {
	try {
		const expires = new Date(
			Date.now() + MAX_AGE_DAYS * 24 * 60 * 60 * 1000,
		).toUTCString();
		document.cookie = `${BUMP_COOKIE}=${show ? "1" : "0"}; expires=${expires}; path=/; SameSite=Lax`;
	} catch {
		// See writeFilters: losing the preference is survivable.
	}
}
